import { Rect, ResizeHandle, TemplateElement } from '../templateEditor.types'

export type SnapRect = Pick<TemplateElement, 'id' | 'w' | 'h'> & { x: number; y: number }

export type ApplySnapOptions = {
    canvasW: number
    canvasH: number
    elements: TemplateElement[]
    showGrid?: boolean
    gridSize?: number
    snapTolerance: number
    snap?: boolean
    excludeIds?: Set<string>
    /** Готовые линии (если переданы — elements не перебираются). */
    vLines?: number[]
    hLines?: number[]
}

export type SnapResult = {
    x: number
    y: number
    verticalGuides: number[]
    horizontalGuides: number[]
}

export type ResizeSnapResult = {
    rect: Rect
    verticalGuides: number[]
    horizontalGuides: number[]
}

const edgeRefsX = (x: number, y: number, w: number, h: number) => [
    { edge: x, offset: 0 },
    { edge: x + w / 2, offset: w / 2 },
    { edge: x + w, offset: w },
]

const edgeRefsY = (x: number, y: number, w: number, h: number) => [
    { edge: y, offset: 0 },
    { edge: y + h / 2, offset: h / 2 },
    { edge: y + h, offset: h },
]

const collectSnapLines = (options: ApplySnapOptions) => {
    const { canvasW, canvasH, elements, showGrid = false, gridSize = 0, excludeIds = new Set<string>() } = options

    if (options.vLines && options.hLines) {
        return { vLines: options.vLines, hLines: options.hLines }
    }

    const vLines = [0, canvasW / 2, canvasW]
    const hLines = [0, canvasH / 2, canvasH]

    if (showGrid && gridSize > 0) {
        for (let gx = 0; gx <= canvasW; gx += gridSize) {
            vLines.push(gx)
        }
        for (let gy = 0; gy <= canvasH; gy += gridSize) {
            hLines.push(gy)
        }
    }

    elements.forEach((other) => {
        if (excludeIds.has(other.id)) {
            return
        }

        vLines.push(other.x, other.x + other.w / 2, other.x + other.w)
        hLines.push(other.y, other.y + other.h / 2, other.y + other.h)
    })

    return { vLines, hLines }
}

/** Предрасчёт линий snap (заморозить на время drag). */
export const buildSnapLines = (options: ApplySnapOptions) => collectSnapLines(options)

const nearestLine = (value: number, lines: number[], tolerance: number) => {
    let bestDist = tolerance + 1
    let bestLine: number | null = null

    for (const line of lines) {
        const dist = Math.abs(value - line)
        if (dist <= tolerance && dist < bestDist) {
            bestDist = dist
            bestLine = line
        }
    }

    return bestLine
}

/** Прилипание к краям холста, сетке и другим элементам; возвращает направляющие. */
export const applySnap = (el: SnapRect, x: number, y: number, options: ApplySnapOptions): SnapResult => {
    const { snapTolerance, snap = true, excludeIds = new Set([el.id]) } = options
    const { vLines, hLines } = collectSnapLines({ ...options, excludeIds })

    const verticalGuides: number[] = []
    const horizontalGuides: number[] = []
    let snapX = x
    let snapY = y

    let bestX = snapTolerance + 1
    for (const line of vLines) {
        for (const ref of edgeRefsX(x, y, el.w, el.h)) {
            const dist = Math.abs(ref.edge - line)
            if (dist <= snapTolerance && dist < bestX) {
                bestX = dist
                if (snap) {
                    snapX = line - ref.offset
                }
                verticalGuides.length = 0
                verticalGuides.push(line)
            }
        }
    }

    const xForY = snap ? snapX : x
    let bestY = snapTolerance + 1
    for (const line of hLines) {
        for (const ref of edgeRefsY(xForY, y, el.w, el.h)) {
            const dist = Math.abs(ref.edge - line)
            if (dist <= snapTolerance && dist < bestY) {
                bestY = dist
                if (snap) {
                    snapY = line - ref.offset
                }
                horizontalGuides.length = 0
                horizontalGuides.push(line)
            }
        }
    }

    return {
        x: snap ? Math.round(snapX) : Math.round(x),
        y: snap ? Math.round(snapY) : Math.round(y),
        verticalGuides,
        horizontalGuides,
    }
}

/** Snap активных краёв при resize + направляющие. */
export const applyResizeSnap = (
    rect: Rect,
    handle: ResizeHandle,
    options: ApplySnapOptions & { minSize: number }
): ResizeSnapResult => {
    const { snapTolerance, snap = true, minSize, excludeIds = new Set<string>() } = options
    const { vLines, hLines } = collectSnapLines({ ...options, excludeIds })

    let { x, y, w, h } = rect
    const verticalGuides: number[] = []
    const horizontalGuides: number[] = []

    const moveLeft = handle.includes('w')
    const moveRight = handle.includes('e')
    const moveTop = handle.includes('n')
    const moveBottom = handle.includes('s')

    if (moveLeft) {
        const line = nearestLine(x, vLines, snapTolerance)
        if (line != null) {
            if (snap) {
                const right = x + w
                x = line
                w = Math.max(minSize, right - x)
            }
            verticalGuides.push(line)
        }
    } else if (moveRight) {
        const line = nearestLine(x + w, vLines, snapTolerance)
        if (line != null) {
            if (snap) {
                w = Math.max(minSize, line - x)
            }
            verticalGuides.push(line)
        }
    } else {
        const centerLine = nearestLine(x + w / 2, vLines, snapTolerance)
        if (centerLine != null) {
            verticalGuides.push(centerLine)
        }
    }

    if (moveTop) {
        const line = nearestLine(y, hLines, snapTolerance)
        if (line != null) {
            if (snap) {
                const bottom = y + h
                y = line
                h = Math.max(minSize, bottom - y)
            }
            horizontalGuides.push(line)
        }
    } else if (moveBottom) {
        const line = nearestLine(y + h, hLines, snapTolerance)
        if (line != null) {
            if (snap) {
                h = Math.max(minSize, line - y)
            }
            horizontalGuides.push(line)
        }
    } else {
        const centerLine = nearestLine(y + h / 2, hLines, snapTolerance)
        if (centerLine != null) {
            horizontalGuides.push(centerLine)
        }
    }

    return {
        rect: {
            x: Math.round(x),
            y: Math.round(y),
            w: Math.round(w),
            h: Math.round(h),
        },
        verticalGuides,
        horizontalGuides,
    }
}
